import type { AnswerKeyItem } from '@ztechapps/quickscore.contract'
import { QuestionTypeEnum } from '@ztechapps/quickscore.contract'
import { isOpenItem } from '../grading/isOpenItem'

/**
 * The shape the model should return for a question of each type — model-facing prose, not the
 * enum's own persisted values (`mcq`, `truefalse`, `identification`, `enumeration`, `Open`),
 * which mean nothing to a vision model reading a prompt.
 */
const QUESTION_TYPE_SHAPE_LABEL: Record<QuestionTypeEnum, string> = {
  [QuestionTypeEnum.Mcq]: 'a single letter A to D',
  [QuestionTypeEnum.TrueFalse]: 'the word True or False',
  [QuestionTypeEnum.Identification]: 'a word or short phrase',
  [QuestionTypeEnum.Enumeration]: 'a comma-separated list',
  [QuestionTypeEnum.Open]: 'a written passage',
}

/**
 * Builds the `{{questionTypes}}` interpolation value for the handwritten extraction prompts —
 * one line per run of consecutive question numbers that share a type, naming the answer shape
 * expected for that run. A shifted row (a student's real Q30 answer landing on Q29's row) puts
 * a word where a single letter belongs, so a row's expected shape is a signal the alignment
 * check (`checkSheetCompleteness`) can compare an actual reading against.
 *
 * An Open range's interior numbers all resolve to the same `AnswerKeyItem`
 * (`buildItemByQuestion`), so they always share a type going in and collapse into one entry
 * naming the range's span — never one line repeating at every number it covers, which would
 * wrongly imply a separate answer at each one.
 */
export const buildQuestionTypeHint = (itemByQuestion: Map<number, AnswerKeyItem>): string => {
  const questionNumbers = Array.from(itemByQuestion.keys()).sort((a, b) => a - b)

  const lines: string[] = []
  let index = 0
  while (index < questionNumbers.length) {
    const startNumber = questionNumbers[index]
    const item = itemByQuestion.get(startNumber) as AnswerKeyItem

    if (isOpenItem(item)) {
      const endNumber = item.endQuestion ?? startNumber
      lines.push(buildLine(startNumber, endNumber, item.questionType))
      // Every number up to endNumber resolves to this same range item — skip them all at once
      // rather than re-visiting each one only to collapse it right back into this line.
      while (index < questionNumbers.length && questionNumbers[index] <= endNumber) {
        index += 1
      }
      continue
    }

    let endNumber = startNumber
    let nextIndex = index + 1
    while (nextIndex < questionNumbers.length && questionNumbers[nextIndex] === endNumber + 1) {
      const nextItem = itemByQuestion.get(questionNumbers[nextIndex]) as AnswerKeyItem
      if (isOpenItem(nextItem) || nextItem.questionType !== item.questionType) {
        break
      }
      endNumber = questionNumbers[nextIndex]
      nextIndex += 1
    }

    lines.push(buildLine(startNumber, endNumber, item.questionType))
    index = nextIndex
  }

  return lines.join('\n')
}

const buildLine = (startNumber: number, endNumber: number, questionType: QuestionTypeEnum): string => {
  const label = startNumber === endNumber ? `Question ${startNumber}` : `Questions ${startNumber}-${endNumber}`
  return `${label}: ${QUESTION_TYPE_SHAPE_LABEL[questionType]}`
}
